import { useMemo } from 'react';
import { Layer } from 'shared/map-exports';
import {
  EvacZoneStatus,
  GeoEventEvacZoneStatus,
  RegionEvacZoneStyle,
} from 'shared/types';
import {
  EvacZonePoi,
  MapboxFeature,
  MapClickGetPois,
  StandardPoi,
} from 'features/mapping/mapping.types';
import { usePoisState } from 'state/usePoisState';
import { useMapLayersState } from 'state/useMapLayersState';
import { useGeoEvent } from 'features/geoEvents/hooks/useGeoEvent';
import { useAppInfo } from 'hooks/useAppInfo';
import {
  BELOW_STREETS_LAYER_ID,
  ON_TOP_OF_EVERYTHING_LAYER_ID,
} from 'features/mapping/styles/constants';
import { getWatchdutyTileUrl } from 'features/mapping/components/layers/layers.utils';
import { useSelectedGeoEventId } from 'features/geoEvents/hooks/useSelectedGeoEventId/useSelectedGeoEventId';
import { AnyGeoEvent } from 'features/geoEvents/anyGeoEvent.types';
import { VisibilityToggledSource } from '../VisibilityToggledSource';
import {
  VIEW_SOURCE_ID,
  FEATURE_UID_V2_FIELD_NAME,
  EVAC_ZONES_VIEW_FILL_LAYER_ID,
  EVAC_ZONES_SOURCE_LAYER_NAME,
  EVAC_ZONES_VIEW_STROKE_LAYER_ID,
  EVAC_ZONES_VIEW_PATTERN_LAYER_ID,
  EVAC_ZONES_VIEW_LABEL_LAYER_ID,
} from './constants';
import { getSelectedEvacZoneUId } from './utils';
import {
  getFillStyle,
  getLabelLayerStyle,
  getPatternStyle,
  getSelectedZonePatternStyle,
  getSelectedZoneStrokeStyle,
  getStrokeStyle,
} from './layerStyles';
import { SelectedEvacZoneOverlay } from './SelectedEvacZoneOverlay';
import { EvacZoneStyles } from '../../../../../constants';

type EvacZonesProperties = {
  display_name: string;
  source_attribution: string;
  uid_v2: string;
  status?: EvacZoneStatus | '';
  evac_zone_style: RegionEvacZoneStyle;
  region_id: number;
  date_created: string;
  date_modified: string;
};

const REFRESH_INTERVAL_SECONDS = 30;

export const activeEvacZoneClickHandler: MapClickGetPois<
  EvacZonesProperties
> = (
  geoJsonFeatures: MapboxFeature<EvacZonesProperties>[],
  activeEvacZones: GeoEventEvacZoneStatus[],
) => {
  return geoJsonFeatures
    .map((feature) => {
      if (!feature || !feature.properties) return null;

      const activeEvacZone = activeEvacZones.find(
        (activeZone) => activeZone.evacZone.uidV2 === feature.properties.uid_v2,
      );

      const poiDialogData: EvacZonePoi = {
        id: Number(feature.properties.uid_v2),
        uid: feature.properties.uid_v2,
        displayName: feature.properties.display_name,
        status: feature.properties.status || undefined,
        dateCreated:
          activeEvacZone?.dateCreated || feature.properties.date_created,
        dateModified:
          activeEvacZone?.dateModified || feature.properties.date_modified,
        source: feature.properties.source_attribution,
        style: feature.properties.evac_zone_style || EvacZoneStyles.default,
      };

      return {
        type: 'activeEvacZone',
        id: poiDialogData.uid,
        metadata: poiDialogData,
      };
    })
    .filter(Boolean) as StandardPoi[];
};

/*
 * This layer is designed to show the zones with statuses globally on the map and/or
 * the active/inactive zones associated with a selected geo event's region(s)
 *
 * It handles the styling by grouping like colors/patterns within the possible groupings of style + status
 * and then filtering on the combinations of UIDs within those color/pattern groupings.
 */
export const StructuredEvacuationsViewLayer = (): JSX.Element => {
  const { selectedPoi } = usePoisState();
  const { isDarkBaseLayer } = useMapLayersState();
  const selectedEvacZoneUId = getSelectedEvacZoneUId(selectedPoi, true);
  const { appVersion } = useAppInfo();

  const { geoEvent: selectedGeoEvent } = useGeoEvent<AnyGeoEvent>({
    id: useSelectedGeoEventId(),
    useCacheBuster: true,
  });

  const selectedRegionIds = useMemo(() => {
    if (
      ['wildfire', 'flooding'].includes(selectedGeoEvent?.geoEventType ?? '') &&
      (selectedGeoEvent?.evacZoneStatuses.length ||
        (selectedGeoEvent?.data &&
          'temporarilyDisplayEvacZones' in selectedGeoEvent.data &&
          selectedGeoEvent.data.temporarilyDisplayEvacZones))
    ) {
      return selectedGeoEvent.regions.map((region) => region.id);
    }
    return [];
  }, [
    selectedGeoEvent?.data,
    selectedGeoEvent?.evacZoneStatuses.length,
    selectedGeoEvent?.geoEventType,
    selectedGeoEvent?.regions,
  ]);

  return (
    <>
      <VisibilityToggledSource
        url={getWatchdutyTileUrl(EVAC_ZONES_SOURCE_LAYER_NAME, {
          app_version: appVersion,
        })}
        visible
        promoteId={FEATURE_UID_V2_FIELD_NAME}
        // Do not unset `volatile` ever for this layer.
        volatile
        id={VIEW_SOURCE_ID}
        refreshIntervalSeconds={REFRESH_INTERVAL_SECONDS}
      >
        <Layer
          id={EVAC_ZONES_VIEW_FILL_LAYER_ID}
          beforeId={BELOW_STREETS_LAYER_ID}
          {...getFillStyle({ regionIds: selectedRegionIds })}
        />
        <Layer
          id={EVAC_ZONES_VIEW_PATTERN_LAYER_ID}
          beforeId={BELOW_STREETS_LAYER_ID}
          {...getPatternStyle()}
        />
        <Layer
          id={EVAC_ZONES_VIEW_STROKE_LAYER_ID}
          beforeId={ON_TOP_OF_EVERYTHING_LAYER_ID}
          {...getStrokeStyle({
            isDarkBaseLayer,
            regionIds: selectedRegionIds,
          })}
        />
        <Layer
          id="selected_pattern_view"
          beforeId={BELOW_STREETS_LAYER_ID}
          {...getSelectedZonePatternStyle(selectedEvacZoneUId)}
        />
        <Layer
          id="selected_stroke_view"
          beforeId={ON_TOP_OF_EVERYTHING_LAYER_ID}
          {...getSelectedZoneStrokeStyle(selectedEvacZoneUId)}
        />
        <Layer
          id={EVAC_ZONES_VIEW_LABEL_LAYER_ID}
          beforeId={ON_TOP_OF_EVERYTHING_LAYER_ID}
          {...getLabelLayerStyle(selectedRegionIds)}
        />
      </VisibilityToggledSource>
      <SelectedEvacZoneOverlay />
    </>
  );
};
